import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, MapPin, LogOut, KeyRound, User as UserIcon } from "lucide-react";
import api from "@/api";
import { useStore, formatINR, formatDateRange } from "@/store/StoreContext";
import Seo from "@/components/store/Seo";
import { toast } from "sonner";

function AuthForm() {
  const { login, register } = useStore();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password, form.phone);
      toast.success(mode === "login" ? "Welcome back!" : "Account created. Welcome to TARISHQ!");
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto tq-card p-8" data-testid="auth-form">
      <div className="flex border-b border-[#EAEAEA] mb-6">
        {["login", "register"].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`auth-tab-${m}`}
            className={`flex-1 py-3 text-xs font-bold uppercase tracking-[0.2em] border-b-2 transition-colors ${mode === m ? "border-[#C59B27]" : "border-transparent text-[#8E8E93]"}`}>
            {m === "login" ? "Login" : "Create Account"}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="register-name" className="tq-input" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Mobile (optional)" data-testid="register-phone" className="tq-input" />
          </>
        )}
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="auth-email" className="tq-input" />
        <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (min 8 chars)" data-testid="auth-password" className="tq-input" />
        <button type="submit" disabled={busy} data-testid="auth-submit-btn" className="tq-btn-dark w-full">
          {busy ? "Please wait…" : mode === "login" ? "Login" : "Create Account"}
        </button>
      </form>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/auth/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([])); }, []);
  if (!orders) return <div className="h-32 bg-[#FAFAFA] animate-pulse" />;
  if (!orders.length) return (
    <div className="text-center py-12" data-testid="no-orders-state">
      <Package size={40} strokeWidth={1.2} className="mx-auto text-[#A8821B] mb-3" />
      <p className="font-serif text-2xl mb-4">No orders yet</p>
      <Link to="/collections/new-arrivals" className="tq-btn-dark">Start Shopping</Link>
    </div>
  );
  return (
    <ul className="space-y-4" data-testid="orders-list">
      {orders.map((o) => (
        <li key={o.id} className="tq-card p-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-semibold">#{o.order_number}</p>
            <p className="text-xs text-[#C59B27] mt-0.5">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {o.items.length} item(s) · {o.payment.mode === "cod" ? "COD" : "Online"}</p>
            {o.delivery_estimate && <p className="text-xs text-[#666666] mt-1">Est. delivery: {formatDateRange(o.delivery_estimate)}</p>}
          </div>
          <div className="text-right">
            <p className="font-semibold">{formatINR(o.total)}</p>
            <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 mt-1 ${o.status === "delivered" ? "bg-[#1A3C34]/10 text-[#1A3C34]" : o.status === "cancelled" ? "bg-[#8B1E21]/10 text-[#8B1E21]" : "bg-[#C59B27]/15 text-[#C59B27]"}`}>{o.status.replace(/_/g, " ")}</span>
            <Link to={`/pages/track-order?order=${o.order_number}`} className="block text-xs text-[#A8821B] underline mt-1.5">Track</Link>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Addresses({ user, reload }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", address: "", apartment: "", city: "", state: "", pin: "", label: "Home" });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try { await api.post("/auth/addresses", form); toast.success("Address saved"); reload(); setForm({ ...form, address: "", city: "", pin: "" }); }
    catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/auth/addresses/${id}`); reload(); };
  return (
    <div>
      {(user.addresses || []).length > 0 && (
        <ul className="grid sm:grid-cols-2 gap-4 mb-6">
          {user.addresses.map((a) => (
            <li key={a.id} className="tq-card p-4 text-sm relative">
              <p className="font-semibold">{a.label}</p>
              <p className="text-[#666666] mt-1">{a.first_name} {a.last_name}<br />{a.address}{a.apartment ? `, ${a.apartment}` : ""}<br />{a.city}, {a.state} — {a.pin}<br />{a.phone}</p>
              <button onClick={() => del(a.id)} data-testid={`address-delete-${a.id}`} className="absolute top-3 right-3 text-xs text-[#8B1E21] underline">Remove</button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="tq-card p-5 grid sm:grid-cols-2 gap-3" data-testid="address-form">
        <p className="sm:col-span-2 font-serif text-xl">Add New Address</p>
        <input required placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="addr-firstname" className="tq-input" />
        <input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="tq-input" />
        <input required placeholder="Mobile" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="addr-phone" className="tq-input" />
        <input required placeholder="PIN code" maxLength={6} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} data-testid="addr-pin" className="tq-input" />
        <input required placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="addr-address" className="tq-input sm:col-span-2" />
        <input required placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="addr-city" className="tq-input" />
        <input required placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="addr-state" className="tq-input" />
        <button type="submit" disabled={busy} data-testid="addr-save-btn" className="tq-btn-dark sm:col-span-2">{busy ? "Saving…" : "Save Address"}</button>
      </form>
    </div>
  );
}

function Profile({ user, reload }) {
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone || "");
  const [pw, setPw] = useState({ current: "", new: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/auth/me", { name, phone }); toast.success("Profile updated"); reload(); }
    catch (err) { toast.error(err.friendlyMessage); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try { await api.put("/auth/password", pw); toast.success("Password changed"); setPw({ current: "", new: "" }); }
    catch (err) { toast.error(err.friendlyMessage); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-6">
      <form onSubmit={save} className="tq-card p-5 space-y-3">
        <p className="font-serif text-xl flex items-center gap-2"><UserIcon size={17} /> Profile</p>
        <input value={name} onChange={(e) => setName(e.target.value)} className="tq-input" data-testid="profile-name" />
        <input value={user.email} disabled className="tq-input opacity-50" />
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile" className="tq-input" data-testid="profile-phone" />
        <button className="tq-btn-dark w-full" data-testid="profile-save-btn">Save</button>
      </form>
      <form onSubmit={changePw} className="tq-card p-5 space-y-3">
        <p className="font-serif text-xl flex items-center gap-2"><KeyRound size={17} /> Change Password</p>
        <input type="password" required placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="tq-input" data-testid="pw-current" />
        <input type="password" required minLength={8} placeholder="New password (min 8)" value={pw.new} onChange={(e) => setPw({ ...pw, new: e.target.value })} className="tq-input" data-testid="pw-new" />
        <button className="tq-btn-outline w-full" data-testid="pw-change-btn">Update Password</button>
      </form>
    </div>
  );
}

export default function Account() {
  const { user, authChecked, logout, refreshWishlist } = useStore();
  const [tab, setTab] = useState("orders");
  const [, setTick] = useState(0);
  const navigate = useNavigate();
  const reload = async () => { try { const { data } = await api.get("/auth/me"); Object.assign(user, data); setTick((t) => t + 1); } catch (e) { /* silent */ } };

  if (!authChecked) return <div className="tq-container py-20"><div className="h-40 bg-[#FAFAFA] animate-pulse max-w-md mx-auto" /></div>;
  if (!user) return (
    <div className="tq-container py-12 sm:py-16" data-testid="account-page">
      <Seo title="Account" noindex />
      <div className="text-center mb-10"><h1 className="font-serif text-4xl sm:text-5xl">Your Account</h1></div>
      <AuthForm />
    </div>
  );

  const TABS = [["orders", "My Orders", Package], ["addresses", "Addresses", MapPin], ["profile", "Profile", UserIcon]];
  return (
    <div className="tq-container py-10 sm:py-14" data-testid="account-dashboard">
      <Seo title="My Account" noindex />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl">Namaste, {user.name.split(" ")[0]}</h1>
          <p className="text-sm text-[#C59B27]">{user.email}</p>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="tq-btn-outline !py-2.5"><LogOut size={14} /> Logout</button>
      </div>
      <div className="flex gap-2 border-b border-[#EAEAEA] mb-8 overflow-x-auto no-scrollbar">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`account-tab-${id}`}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 whitespace-nowrap transition-colors ${tab === id ? "border-[#C59B27] text-[#111111]" : "border-transparent text-[#8E8E93]"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
        <Link to="/wishlist" className="flex items-center gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#8E8E93] hover:text-[#111111]">Wishlist</Link>
      </div>
      {tab === "orders" && <Orders />}
      {tab === "addresses" && <Addresses user={user} reload={reload} />}
      {tab === "profile" && <Profile user={user} reload={reload} />}
    </div>
  );
}
